<template>
  <div class="ur-sel-panel">
    <div class="ur-sel-panel-body">
    <!-- 選配須知（設計稿：單一段落＋<br>，截止日粗體）-->
    <div class="ur-sel-section ur-sel-intro">
      <p class="ur-sel-notice-line" v-html="noticeHtml"></p>
    </div>

    <!-- 基本資料 -->
    <div class="ur-sel-section">
      <div class="ur-sel-section-title">{{ $t('URBANRENEWAL_SELECT.PANEL.BASIC_INFO') }}</div>

      <div
        v-for="(o, i) in owners"
        :key="o.id"
        class="ur-sel-owner-row"
      >
        <label class="ur-sel-check">
          <input
            type="checkbox"
            :checked="checkedOwnerIds.includes(o.id)"
            @change="toggleOwner(o.id)"
          />
          <span class="ur-sel-check-box"></span>
        </label>
        <span class="ur-sel-owner-label">{{ $t('URBANRENEWAL_SELECT.PANEL.OWNER_LABEL', { no: ownerNo(i) }) }}</span>
        <div class="ur-sel-field ur-sel-field-plain">{{ o.display_name }}</div>
      </div>

      <div class="ur-sel-info-row">
        <span class="ur-sel-info-label">{{ $t('URBANRENEWAL_SELECT.PANEL.ALLOCATED') }}</span>
        <div class="ur-sel-field ur-sel-field-plain">{{ fmtMoney(allocatedTotal) }}</div>
      </div>
      <div class="ur-sel-info-row">
        <span class="ur-sel-info-label">{{ $t('URBANRENEWAL_SELECT.PANEL.SELECTABLE') }}</span>
        <div class="ur-sel-field ur-sel-field-plain">{{ fmtMoney(selectableTotal) }}</div>
      </div>
    </div>

    <!-- 選配試算清單 -->
    <div class="ur-sel-section">
      <div class="ur-sel-section-head">
        <div class="ur-sel-section-title">{{ $t('URBANRENEWAL_SELECT.PANEL.SELECTION_LIST') }}</div>
        <button
          v-if="isMobile"
          class="ur-sel-add-btn ur-sel-goto-grid"
          type="button"
          @click="$emit('show-grid')"
        >{{ $t('URBANRENEWAL_SELECT.PANEL.MOBILE_VIEW_GRID') }}</button>
      </div>

      <!-- 欄位標題（有任何列時顯示）-->
      <div v-if="houseRows.length || parkingRows.length" class="ur-sel-list-head">
        <span></span>
        <span class="ur-sel-list-head-code">{{ $t('URBANRENEWAL_SELECT.PANEL.COL_UNIT_CODE') }}</span>
        <span class="ur-sel-list-head-count">{{ $t('URBANRENEWAL_SELECT.PANEL.COL_GROUP_COUNT') }}</span>
        <span></span>
      </div>

      <!-- 房屋列：每列＝可搜尋下拉（A 案：已選列亦可改選）-->
      <div v-if="houseRows.length" class="ur-sel-list">
        <div v-for="(row, i) in houseRows" :key="row.key" class="ur-sel-srow">
          <span class="ur-sel-srow-label">{{ $t('URBANRENEWAL_SELECT.PANEL.HOUSE_ROW', { no: ownerNo(i) }) }}</span>
          <div class="ur-sel-srow-pick">
            <div
              class="ur-sel-srow-field ur-sel-srow-trigger"
              :class="{ 'is-open': openPicker === row.key }"
              @click="togglePicker(row.key)"
            >
              <span class="ur-sel-srow-code" :class="{ 'is-placeholder': !row.unit }">
                {{ row.unit ? row.unit.unit_code : $t('URBANRENEWAL_SELECT.PANEL.UNIT_PH_HOUSE') }}
              </span>
              <span class="ur-sel-srow-caret">&#9662;</span>
            </div>
            <template v-if="openPicker === row.key">
              <div class="ur-sel-add-backdrop" @click="closePicker"></div>
              <div class="ur-sel-add-pop">
                <div class="ur-sel-add-search">
                  <input ref="pickerInput" v-model="pickerSearch" class="ur-sel-add-search-input" :placeholder="$t('URBANRENEWAL_SELECT.PANEL.SEARCH')" />
                </div>
                <div class="ur-sel-add-list">
                  <div v-if="filteredRowOptions('housing', row.unit).length === 0" class="ur-sel-add-empty">
                    {{ $t('URBANRENEWAL_SELECT.PANEL.NO_OPTIONS') }}
                  </div>
                  <div
                    v-for="opt in filteredRowOptions('housing', row.unit)"
                    :key="opt.id"
                    class="ur-sel-add-item"
                    @click="pickForRow('housing', row.unit, opt)"
                  >{{ opt.unit_code }}</div>
                </div>
              </div>
            </template>
          </div>
          <div class="ur-sel-srow-count">
            {{ row.unit ? ((row.unit.selection_count || 0) + ' ' + $t('URBANRENEWAL_SELECT.GRID.UNIT_GROUP')) : '－' }}
          </div>
          <button
            v-if="row.unit || selectedHousing.length"
            class="ur-sel-srow-remove"
            type="button"
            @click="removeRow('housing', row.unit)"
          >&times;</button>
        </div>
      </div>

      <div class="ur-sel-add">
        <button class="ur-sel-add-btn" type="button" @click="addRow('housing')">
          <span class="ur-sel-add-icon">+</span>{{ $t('URBANRENEWAL_SELECT.PANEL.ADD_HOUSE') }}
        </button>
      </div>

      <!-- 車位列 -->
      <div v-if="parkingRows.length" class="ur-sel-list ur-sel-list-parking">
        <div v-for="(row, i) in parkingRows" :key="row.key" class="ur-sel-srow">
          <span class="ur-sel-srow-label">{{ $t('URBANRENEWAL_SELECT.PANEL.PARKING_ROW', { no: ownerNo(i) }) }}</span>
          <div class="ur-sel-srow-pick">
            <div
              class="ur-sel-srow-field ur-sel-srow-trigger"
              :class="{ 'is-open': openPicker === row.key }"
              @click="togglePicker(row.key)"
            >
              <span class="ur-sel-srow-code" :class="{ 'is-placeholder': !row.unit }">
                {{ row.unit ? row.unit.unit_code : $t('URBANRENEWAL_SELECT.PANEL.UNIT_PH_PARK') }}
              </span>
              <span class="ur-sel-srow-caret">&#9662;</span>
            </div>
            <template v-if="openPicker === row.key">
              <div class="ur-sel-add-backdrop" @click="closePicker"></div>
              <div class="ur-sel-add-pop">
                <div class="ur-sel-add-search">
                  <input ref="pickerInput" v-model="pickerSearch" class="ur-sel-add-search-input" :placeholder="$t('URBANRENEWAL_SELECT.PANEL.SEARCH')" />
                </div>
                <div class="ur-sel-add-list">
                  <div v-if="filteredRowOptions('parking', row.unit).length === 0" class="ur-sel-add-empty">
                    {{ $t('URBANRENEWAL_SELECT.PANEL.NO_OPTIONS') }}
                  </div>
                  <div
                    v-for="opt in filteredRowOptions('parking', row.unit)"
                    :key="opt.id"
                    class="ur-sel-add-item"
                    @click="pickForRow('parking', row.unit, opt)"
                  >{{ opt.unit_code }}</div>
                </div>
              </div>
            </template>
          </div>
          <div class="ur-sel-srow-count">
            {{ row.unit ? ((row.unit.selection_count || 0) + ' ' + $t('URBANRENEWAL_SELECT.GRID.UNIT_GROUP')) : '－' }}
          </div>
          <button
            v-if="row.unit || selectedParking.length"
            class="ur-sel-srow-remove"
            type="button"
            @click="removeRow('parking', row.unit)"
          >&times;</button>
        </div>
      </div>

      <div class="ur-sel-add">
        <button class="ur-sel-add-btn" type="button" @click="addRow('parking')">
          <span class="ur-sel-add-icon">+</span>{{ $t('URBANRENEWAL_SELECT.PANEL.ADD_PARKING') }}
        </button>
      </div>

      <!-- 選配價值總計（去框、置於底線之上）-->
      <div class="ur-sel-total-line">
        <span class="ur-sel-total-label">{{ $t('URBANRENEWAL_SELECT.PANEL.SELECT_TOTAL') }}</span>
        <span class="ur-sel-total-value">{{ fmtMoney(actualTotal) }}</span>
      </div>
    </div>

    <!-- 找補試算（設計稿：算式呈現，＝差額正綠負紅；選配價值總計已上移至選配試算區）-->
    <div class="ur-sel-section">
      <!-- 找補試算：可選配價值 － 選配價值總計 ＝ 可領回/應補差額 -->
      <div class="ur-sel-makeup">
        <div class="ur-sel-makeup-title">{{ $t('URBANRENEWAL_SELECT.PANEL.MAKEUP_TITLE') }}</div>
        <div class="ur-sel-makeup-formula">
          {{ noColon($t('URBANRENEWAL_SELECT.PANEL.SELECTABLE')) }}
          <strong>{{ fmtMoney(selectableTotal) }}</strong>
          <span class="ur-sel-makeup-op">－</span>
          {{ noColon($t('URBANRENEWAL_SELECT.PANEL.SELECT_TOTAL')) }}
          <strong>{{ fmtMoney(actualTotal) }}</strong>
        </div>
        <div
          class="ur-sel-makeup-result"
          :class="makeupStatus === 'pay' ? 'is-pay' : 'is-refund'"
        >
          ＝ {{ noColon(makeupLabel) }} <strong>{{ fmtMoney(Math.abs(makeup)) }}</strong>
        </div>
      </div>
    </div>

    <!-- 提示 / 免責（設計稿）-->
    <div class="ur-sel-notes">
      <div class="ur-sel-amount-note">{{ $t('URBANRENEWAL_SELECT.PANEL.AMOUNT_NOTE') }}</div>
      <div class="ur-sel-disclaimer">{{ $t('URBANRENEWAL_SELECT.PANEL.DISCLAIMER') }}</div>
    </div>

    <!-- 儲存/匯出提醒（設計稿：置於免責紅字之後）-->
    <div v-if="errorMsg" class="ur-lo-error">
      <span>{{ errorMsg }}</span>
      <button class="ur-lo-error-close" @click="errorMsg = ''">&#10007;</button>
    </div>
    </div>
    <!-- /ur-sel-panel-body -->

    <!-- 底部固定列 -->
    <div class="ur-sel-footer">
      <button class="ur-sel-btn ur-sel-btn-ghost" @click="clearAll">
        {{ $t('URBANRENEWAL_SELECT.PANEL.CLEAR_ALL') }}
      </button>
      <button class="ur-sel-btn ur-sel-btn-primary ur-sel-btn-save" :disabled="saving || isOverSelect" @click="save">
        {{ $t('URBANRENEWAL_SELECT.PANEL.SAVE') }}
      </button>
    </div>

    <!-- 儲存完成浮動提示（懸浮於按鈕列上方，不需捲到底即可見；數秒後自動消失）-->
    <transition name="ur-sel-toast">
      <div v-if="successMsg" class="ur-sel-toast-success">
        <svg class="ur-sel-toast-icon" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm-1.1 14.3l-4.2-4.2 1.4-1.4 2.8 2.8 5.8-5.8 1.4 1.4-7.2 7.2z"/>
        </svg>
        <span>{{ successMsg }}</span>
      </div>
    </transition>

    <!-- 儲存下載：先顯示本次選配結果＋紙本提醒，確定後存檔並下載結果圖 -->
    <SaveConfirmDialog
      v-if="saveConfirmVisible"
      :owners="checkedOwners"
      :housing="selectedHousing"
      :parking="selectedParking"
      :total="actualTotal"
      :makeup="makeup"
      :makeup-status="makeupStatus"
      @confirm="onSaveConfirm"
      @close="saveConfirmVisible = false"
    />

    <!-- 非優先選配區確認/重選（FE-P08；與熱度圖 RightPanel 一致）-->
    <NonPriorityDialog
      v-if="nonPriorityVisible"
      @confirm="confirmNonPriority"
      @reselect="cancelNonPriority"
      @close="cancelNonPriority"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import SaveConfirmDialog from './SaveConfirmDialog.vue';
import NonPriorityDialog from './NonPriorityDialog.vue';
import moneyFormat from '../moneyFormat';

export default {
  name: 'UrbanRenewalSelectLeftPanel',

  mixins: [moneyFormat],

  components: {
    SaveConfirmDialog,
    NonPriorityDialog,
  },

  props: {
    isMobile: { type: Boolean, default: false },
  },

  data() {
    return {
      saving: false,
      errorMsg: '',
      successMsg: '',
      saveConfirmVisible: false,
      draftHousing: false,   // 是否有一列空的房屋草稿（同型別最多一列）
      draftParking: false,   // 是否有一列空的車位草稿
      openPicker: null,      // 目前展開的列 picker key（'housing:<id>' / 'housing:draft' / 'parking:...'）
      pickerSearch: '',
      nonPriorityVisible: false,   // 選到非優先房屋時，先跳警告（與熱度圖一致）
      pendingSelect: null,         // 非優先待確認：{ type, oldId, unit, wasDraft }
    };
  },

  computed: {
    ...mapState('urbanRenewalSelect', ['owners', 'checkedOwnerIds', 'selectedHousing', 'selectedParking', 'grid', 'lastUpdated', 'dirty', 'project']),
    ...mapGetters('urbanRenewalSelect', [
      'checkedOwners',
      'allocatedTotal',
      'selectableTotal',
      'actualTotal',
      'makeup',
      'makeupStatus',
      'isOverSelect',
    ]),

    // 清單列 = 已選單元（可改選）＋ 空列。
    // 空列出現條件：該型別完全沒選（保底一列，含儲存過的）或使用者按了「新增」。
    houseRows() {
      const rows = this.selectedHousing.map(u => ({ type: 'housing', unit: u, key: 'housing:' + u.id }));
      if (rows.length === 0 || this.draftHousing) rows.push({ type: 'housing', unit: null, key: 'housing:draft' });
      return rows;
    },

    parkingRows() {
      const rows = this.selectedParking.map(u => ({ type: 'parking', unit: u, key: 'parking:' + u.id }));
      if (rows.length === 0 || this.draftParking) rows.push({ type: 'parking', unit: null, key: 'parking:draft' });
      return rows;
    },

    deadline() {
      return (this.project && this.project.selection_deadline) || '—';
    },

    makeupLabel() {
      if (this.makeupStatus === 'refund') return this.$t('URBANRENEWAL_SELECT.PANEL.MAKEUP_REFUND');
      if (this.makeupStatus === 'pay') return this.$t('URBANRENEWAL_SELECT.PANEL.MAKEUP_PAY');
      return this.$t('URBANRENEWAL_SELECT.PANEL.MAKEUP_NONE');
    },

    // 選配須知：單一段落＋<br>（設計稿結構），截止日以粗體呈現（deadline 為後端日期字串，可信）
    noticeHtml() {
      const l1 = this.$t('URBANRENEWAL_SELECT.PANEL.INTRO_LINE1', { deadline: `<strong>${this.deadline}</strong>` });
      const l2 = this.$t('URBANRENEWAL_SELECT.PANEL.INTRO_LINE2');
      return l1 + '<br>' + l2;
    },
  },

  methods: {
    ...mapMutations('urbanRenewalSelect', [
      'TOGGLE_OWNER',
      'REMOVE_HOUSING',
      'REMOVE_PARKING',
      'CLEAR_SELECTIONS',
      'SET_LAST_UPDATED',
      'SET_DIRTY',
    ]),

    ownerNo(i) {
      return String(i + 1).padStart(2, '0');   // 地主01 / 02 …（設計稿標籤）
    },

    // 去掉標籤尾端冒號（i18n 標籤原本帶「：」，算式內用純標籤）；相容全形/半形冒號
    noColon(s) {
      return String(s == null ? '' : s).replace(/[：:]\s*$/, '');
    },

    toggleOwner(id) {
      this.TOGGLE_OWNER(id);
    },

    // 該列下拉可選項目 = 全格子扣掉已選（但保留本列目前單元，讓它能顯示/改選）
    rowOptions(type, currentUnit) {
      const grid = type === 'housing' ? this.grid.housing : this.grid.parking;
      const selected = type === 'housing' ? this.selectedHousing : this.selectedParking;
      return grid.filter(u => (currentUnit && u.id === currentUnit.id) || !selected.some(s => s.id === u.id));
    },

    filteredRowOptions(type, currentUnit) {
      const q = this.pickerSearch.trim().toLowerCase();
      const list = this.rowOptions(type, currentUnit);
      if (!q) return list;
      return list.filter(u => String(u.unit_code).toLowerCase().indexOf(q) !== -1);
    },

    togglePicker(key) {
      if (this.openPicker === key) { this.closePicker(); return; }
      this.openPicker = key;
      this.pickerSearch = '';
      this.$nextTick(() => {
        let el = this.$refs.pickerInput;
        if (Array.isArray(el)) el = el[0];
        if (el) el.focus();
      });
    },

    closePicker() {
      this.openPicker = null;
      this.pickerSearch = '';
    },

    // 「新增房屋/車位」只補一列空列（同型別最多一列草稿），並展開其下拉
    addRow(type) {
      if (type === 'housing') { this.draftHousing = true; this.togglePicker('housing:draft'); }
      else { this.draftParking = true; this.togglePicker('parking:draft'); }
    },

    pickForRow(type, currentUnit, unit) {
      this.closePicker();
      if (currentUnit && currentUnit.id === unit.id) return;   // 沒變更
      // 非優先房屋先攔截確認/重選（與熱度圖一致）
      if (type === 'housing' && unit.is_priority === false) {
        this.pendingSelect = { type, oldId: currentUnit ? currentUnit.id : null, unit, wasDraft: !currentUnit };
        this.nonPriorityVisible = true;
        return;
      }
      this.commitSelect(type, currentUnit ? currentUnit.id : null, unit, !currentUnit);
    },

    async commitSelect(type, oldId, unit, wasDraft) {
      // 改選 → 原地替換（維持列順序，超選則舊的原地保留）；草稿 → 新增。皆走 store 統一 110% 守門。
      const ok = oldId != null
        ? await this.$store.dispatch('urbanRenewalSelect/replaceUnit', { type, oldId, unit })
        : await this.$store.dispatch('urbanRenewalSelect/addUnit', { type, unit });
      if (ok && wasDraft) {
        if (type === 'housing') this.draftHousing = false; else this.draftParking = false;
      }
    },

    removeRow(type, currentUnit) {
      this.closePicker();
      if (currentUnit) {
        if (type === 'housing') this.REMOVE_HOUSING(currentUnit.id); else this.REMOVE_PARKING(currentUnit.id);
      } else {
        if (type === 'housing') this.draftHousing = false; else this.draftParking = false;
      }
    },

    confirmNonPriority() {
      if (this.pendingSelect) {
        const p = this.pendingSelect;
        this.commitSelect(p.type, p.oldId, p.unit, p.wasDraft);
      }
      this.nonPriorityVisible = false;
      this.pendingSelect = null;
    },

    cancelNonPriority() {
      this.nonPriorityVisible = false;
      this.pendingSelect = null;
    },

    clearAll() {
      this.CLEAR_SELECTIONS();
      this.draftHousing = false;
      this.draftParking = false;
      this.closePicker();
    },

    save() {
      // FE-P08：儲存前先開儲存警語，確認後才真正 performSave
      this.saveConfirmVisible = true;
    },

    async onSaveConfirm() {
      this.saveConfirmVisible = false;
      const ok = await this.performSave();     // 儲存下載：先存後端 draft
      if (ok) this.downloadResultImage();      // 成功才下載結果圖（.jpg）
    },

    async performSave() {
      this.saving = true;
      this.errorMsg = '';
      this.successMsg = '';
      try {
        const payload = {
          housing_unit_ids: this.selectedHousing.map(u => u.id),
          parking_unit_ids: this.selectedParking.map(u => u.id),
          checked_owner_ids: this.checkedOwnerIds,
        };
        const res = await this.$store.dispatch('urbanRenewalSelect/saveDraft', payload);
        this.SET_LAST_UPDATED(res.data.last_updated);
        this.SET_DIRTY(false);   // 已存 → 清除未儲存旗標
        this.successMsg = this.$t('URBANRENEWAL_SELECT.PANEL.SAVE_SUCCESS');
        setTimeout(() => { this.successMsg = ''; }, 3000);
        return true;
      } catch (e) {
        this.errorMsg =
          (e.response && e.response.data && e.response.data.detail) ||
          this.$t('URBANRENEWAL_SELECT.PANEL.SAVE_FAIL');
        return false;
      } finally {
        this.saving = false;
      }
    },

    // 產生「本次選配結果」圖並下載（.jpg，存手機相簿／電腦資料夾）
    downloadResultImage() {
      const W = 820, scale = 2;
      const fmtN = v => Number(v || 0).toLocaleString('zh-TW', { maximumFractionDigits: 0 });
      const ownerNames = this.checkedOwners.map(o => o.display_name).join('、') || '-';
      const lines = [];
      this.selectedHousing.forEach(u => lines.push([this.$t('URBANRENEWAL_SELECT.DIALOG.RESULT_HOUSE') + '　' + u.unit_code, fmtN(u.total_price) + ' 元']));
      this.selectedParking.forEach(u => lines.push([this.$t('URBANRENEWAL_SELECT.DIALOG.RESULT_PARK') + '　' + u.unit_code, fmtN(u.value) + ' 元']));
      const H = 640 + lines.length * 38;
      const c = document.createElement('canvas');
      c.width = W * scale; c.height = H * scale;
      const g = c.getContext('2d');
      g.scale(scale, scale);
      g.fillStyle = '#ffffff'; g.fillRect(0, 0, W, H);
      const FONT = '"Noto Sans TC","PingFang TC","Microsoft JhengHei",sans-serif';
      let y = 56;
      g.fillStyle = '#004982'; g.font = '700 26px ' + FONT;
      g.fillText((this.project && this.project.project_name ? this.project.project_name + ' ' : '') + this.$t('URBANRENEWAL_SELECT.DIALOG.RESULT_TITLE'), 48, y);
      y += 34;
      g.fillStyle = '#888888'; g.font = '14px ' + FONT;
      g.fillText(this.$t('URBANRENEWAL_SELECT.DIALOG.RESULT_TIME', { t: new Date().toLocaleString('zh-TW') }), 48, y);
      y += 30;
      const hr = () => { g.strokeStyle = '#e8e8e8'; g.beginPath(); g.moveTo(48, y); g.lineTo(W - 48, y); g.stroke(); y += 26; };
      const row = (label, value, opts) => {
        opts = opts || {};
        g.fillStyle = opts.color || '#444444';
        g.font = (opts.bold ? '700 ' : '') + (opts.size || 16) + 'px ' + FONT;
        g.textAlign = 'left'; g.fillText(label, 48, y);
        g.textAlign = 'right'; g.fillText(value, W - 48, y);
        g.textAlign = 'left';
        y += opts.gap || 38;
      };
      hr();
      row(this.$t('URBANRENEWAL_SELECT.DIALOG.RESULT_OWNER'), ownerNames, { bold: true });
      row(this.noColon(this.$t('URBANRENEWAL_SELECT.PANEL.ALLOCATED')), fmtN(this.allocatedTotal) + ' 元');
      row(this.noColon(this.$t('URBANRENEWAL_SELECT.PANEL.SELECTABLE')), fmtN(this.selectableTotal) + ' 元');
      y -= 12; hr();
      if (lines.length === 0) row(this.$t('URBANRENEWAL_SELECT.DIALOG.RESULT_EMPTY'), '', { color: '#999999' });
      else lines.forEach(l => row(l[0], l[1]));
      y -= 12; hr();
      row(this.noColon(this.$t('URBANRENEWAL_SELECT.PANEL.SELECT_TOTAL')), fmtN(this.actualTotal) + ' 元', { bold: true, size: 18 });
      row(this.noColon(this.makeupLabel), fmtN(Math.abs(this.makeup)) + ' 元', { bold: true, size: 18, color: this.makeupStatus === 'pay' ? '#ED4242' : '#127E2D' });
      y += 8;
      g.fillStyle = '#ED4242'; g.font = '15px ' + FONT;
      y = this.wrapText(g, this.$t('URBANRENEWAL_SELECT.DIALOG.SAVE_BODY'), 48, y, W - 96, 24);
      y += 8;
      g.fillStyle = '#999999'; g.font = '14px ' + FONT;
      this.wrapText(g, this.$t('URBANRENEWAL_SELECT.PANEL.DISCLAIMER'), 48, y, W - 96, 22);

      const a = document.createElement('a');
      a.href = c.toDataURL('image/jpeg', 0.92);
      a.download = this.$t('URBANRENEWAL_SELECT.DIALOG.RESULT_TITLE') + '_' + (this.checkedOwners.map(o => o.display_name).join('') || '') + '.jpg';
      document.body.appendChild(a); a.click(); a.remove();
    },

    // canvas 文字自動換行（依字元寬度）
    wrapText(g, text, x, y, maxWidth, lineHeight) {
      const chars = String(text == null ? '' : text).split('');
      let line = '';
      for (let i = 0; i < chars.length; i++) {
        const test = line + chars[i];
        if (g.measureText(test).width > maxWidth && line) {
          g.fillText(line, x, y);
          line = chars[i];
          y += lineHeight;
        } else {
          line = test;
        }
      }
      if (line) { g.fillText(line, x, y); y += lineHeight; }
      return y;
    },
  },
};
</script>
